/**
 * CodigoGenerator - Generador automático de códigos de ética
 * Features:
 * - Selección de chequeo + encuesta
 * - Configuración de plantilla
 * - Preview de generación
 * - Generación automática
 */
import { useState, useEffect } from 'react';
import { 
  FileCheck, 
  FileText, 
  Sparkles, 
  Eye,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { codigosAPI, chequeoAPI, encuestasAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const CodigoGenerator = ({ onSuccess, onCancel }) => {
  const { user } = useAuthStore();

  // Estado
  const [step, setStep] = useState(1); // 1: Selección, 2: Configuración, 3: Preview
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Datos para generación
  const [evaluacionesChequeo, setEvaluacionesChequeo] = useState([]);
  const [instanciasEncuesta, setInstanciasEncuesta] = useState([]);
  const [selectedChequeo, setSelectedChequeo] = useState(null);
  const [selectedEncuesta, setSelectedEncuesta] = useState(null);

  // Configuración
  const [config, setConfig] = useState({
    nombre: '',
    plantilla: 'estandar',
    incluir_estadisticas: true,
    incluir_recomendaciones: true,
  });

  // Preview
  const [preview, setPreview] = useState(null);

  // Cargar datos
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar evaluaciones completadas
      const [chequeoRes, encuestaRes] = await Promise.all([
        chequeoAPI.getEvaluaciones({ completada: true, limit: 20 }),
        encuestasAPI.getInstancias({ activa: false, limit: 20 }),
      ]);

      setEvaluacionesChequeo(chequeoRes.data.items || chequeoRes.data);
      setInstanciasEncuesta(encuestaRes.data.items || encuestaRes.data);
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleNextStep = () => {
    if (step === 1) {
      // Validar selección
      if (!selectedChequeo && !selectedEncuesta) {
        setError('Debes seleccionar al menos una evaluación');
        return;
      }
      
      // Auto-generar nombre si está vacío
      if (!config.nombre) {
        const fecha = new Date().toLocaleDateString('es-ES', { 
          month: 'short', 
          year: 'numeric' 
        });
        setConfig({ ...config, nombre: `Código de Ética ${fecha}` });
      }
    }

    if (step === 2) {
      // Validar configuración
      if (!config.nombre.trim()) {
        setError('El nombre es requerido');
        return;
      }
      
      // Cargar preview
      loadPreview();
    }

    setError(null);
    setStep(step + 1);
  };

  const handlePrevStep = () => {
    setStep(step - 1);
    setError(null);
  };

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = {
        nombre: config.nombre,
        id_evaluacion_chequeo: selectedChequeo?.id_evaluacion,
        id_instancia_encuesta: selectedEncuesta?.id_instancia,
        plantilla: config.plantilla,
      };

      // Generar preview (sin guardar)
      const response = await codigosAPI.generar(data, false);
      setPreview(response.data);
    } catch (err) {
      console.error('Error al generar preview:', err);
      setError(err.message || 'Error al generar preview');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerar = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = {
        nombre: config.nombre,
        id_evaluacion_chequeo: selectedChequeo?.id_evaluacion,
        id_instancia_encuesta: selectedEncuesta?.id_instancia,
        plantilla: config.plantilla,
        incluir_estadisticas: config.incluir_estadisticas,
        incluir_recomendaciones: config.incluir_recomendaciones,
      };

      // Generar y guardar
      await codigosAPI.generar(data, true);
      
      setSuccess('Código de ética generado correctamente');
      
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1000);
      }
    } catch (err) {
      console.error('Error al generar código:', err);
      setError(err.message || 'Error al generar código');
    } finally {
      setLoading(false);
    }
  };

  if (loading && step === 1) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando evaluaciones..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Indicador de pasos */}
      <div className="flex items-center justify-between mb-8">
        {[
          { num: 1, label: 'Selección' },
          { num: 2, label: 'Configuración' },
          { num: 3, label: 'Preview' },
        ].map((s, index) => (
          <div key={s.num} className="flex items-center flex-1">
            <div className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= s.num
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > s.num ? <CheckCircle className="h-6 w-6" /> : s.num}
              </div>
              <span
                className={`ml-2 text-sm font-medium ${
                  step >= s.num ? 'text-gray-900' : 'text-gray-500'
                }`}
              >
                {s.label}
              </span>
            </div>
            {index < 2 && (
              <div
                className={`flex-1 h-1 mx-4 ${
                  step > s.num ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Paso 1: Selección de Evaluaciones */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Instrucciones:</p>
                <p>Selecciona al menos una evaluación completada para generar el código de ética. El sistema utilizará los resultados para crear contenido personalizado.</p>
              </div>
            </div>
          </div>

          {/* Lista de Chequeo */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Lista de Chequeo
            </h3>
            {evaluacionesChequeo.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <FileCheck className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No hay evaluaciones completadas</p>
              </div>
            ) : (
              <div className="space-y-2">
                {evaluacionesChequeo.map((evaluacion) => (
                  <div
                    key={evaluacion.id_evaluacion}
                    onClick={() => setSelectedChequeo(evaluacion)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedChequeo?.id_evaluacion === evaluacion.id_evaluacion
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileCheck
                          className={`h-5 w-5 ${
                            selectedChequeo?.id_evaluacion === evaluacion.id_evaluacion
                              ? 'text-primary-600'
                              : 'text-gray-400'
                          }`}
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            Evaluación {evaluacion.id_evaluacion}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(evaluacion.fecha_evaluacion).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                      </div>
                      {selectedChequeo?.id_evaluacion === evaluacion.id_evaluacion && (
                        <CheckCircle className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Encuestas */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Encuestas
            </h3>
            {instanciasEncuesta.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No hay encuestas cerradas</p>
              </div>
            ) : (
              <div className="space-y-2">
                {instanciasEncuesta.map((inst) => (
                  <div
                    key={inst.id_instancia}
                    onClick={() => setSelectedEncuesta(inst)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedEncuesta?.id_instancia === inst.id_instancia
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText
                          className={`h-5 w-5 ${
                            selectedEncuesta?.id_instancia === inst.id_instancia
                              ? 'text-primary-600'
                              : 'text-gray-400'
                          }`}
                        />
                        <div>
                          <p className="font-medium text-gray-900">
                            {inst.nombre}
                          </p>
                          <p className="text-sm text-gray-500">
                            {inst.participantes_completados} participantes
                          </p>
                        </div>
                      </div>
                      {selectedEncuesta?.id_instancia === inst.id_instancia && (
                        <CheckCircle className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Paso 2: Configuración */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Código *
            </label>
            <input
              type="text"
              value={config.nombre}
              onChange={(e) => setConfig({ ...config, nombre: e.target.value })}
              placeholder="Ej: Código de Ética 2025"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plantilla
            </label>
            <select
              value={config.plantilla}
              onChange={(e) => setConfig({ ...config, plantilla: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="estandar">Estándar</option>
              <option value="completa">Completa</option>
              <option value="simplificada">Simplificada</option>
            </select>
            <p className="text-sm text-gray-500 mt-1">
              La plantilla estándar incluye las secciones principales
            </p>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.incluir_estadisticas}
                onChange={(e) =>
                  setConfig({ ...config, incluir_estadisticas: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Incluir estadísticas de evaluaciones
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.incluir_recomendaciones}
                onChange={(e) =>
                  setConfig({ ...config, incluir_recomendaciones: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Incluir recomendaciones basadas en resultados
              </span>
            </label>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Resumen</h4>
            <div className="space-y-1 text-sm text-gray-600">
              {selectedChequeo && (
                <div className="flex items-center gap-2">
                  <FileCheck className="h-4 w-4" />
                  <span>Lista de Chequeo seleccionada</span>
                </div>
              )}
              {selectedEncuesta && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span>Encuesta: {selectedEncuesta.nombre}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Paso 3: Preview */}
      {step === 3 && (
        <div className="space-y-6">
          {loading ? (
            <div className="p-8">
              <Loading size="lg" text="Generando preview..." />
            </div>
          ) : preview ? (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="h-6 w-6 text-primary-600" />
                  <h3 className="text-xl font-bold text-primary-900">
                    Preview del Código
                  </h3>
                </div>
                <p className="text-primary-800">
                  El código ha sido generado automáticamente basándose en tus evaluaciones.
                </p>
              </div>

              {/* Contenido del preview */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 max-h-96 overflow-y-auto">
                <div className="prose prose-sm max-w-none">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    {preview.nombre}
                  </h1>
                  
                  {preview.secciones?.map((seccion, index) => (
                    <div key={index} className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-2">
                        {seccion.titulo}
                      </h2>
                      <p className="text-gray-700 whitespace-pre-line">
                        {seccion.contenido}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-900">
                    <p className="font-medium mb-1">Nota:</p>
                    <p>Este es un preview del código. Podrás editarlo después de guardarlo como borrador.</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Botones de navegación */}
      <div className="flex gap-3 justify-end pt-4 border-t">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
        )}

        {step > 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevStep}
            disabled={loading}
          >
            Anterior
          </Button>
        )}

        {step < 3 ? (
          <Button
            type="button"
            variant="primary"
            onClick={handleNextStep}
            disabled={loading}
          >
            Siguiente
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={handleGenerar}
            disabled={loading}
            loading={loading}
            icon={Sparkles}
          >
            Generar Código
          </Button>
        )}
      </div>
    </div>
  );
};

export default CodigoGenerator;
